import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Document} from "../../../model/document";
import {DocumentStatus} from "../../../model/documentStatus";
import {ProductService} from "../../../service/product.service";
import {Product} from "../../../model/product";
import {Order} from "../../../model/order";
import {forkJoin, Subject} from "rxjs";
import {DocumentClass} from "../../../model/documentClass";
import {DocumentFile} from "../../../model/document-file";
import {DownloadService} from "../../../service/download.service";
import {Property} from "../../../model/property";
import NameFinderUtils from "../../../utils/name-finder-utils";
import {StatusMessageService} from "../../../service/status-message.service";
import {StatusMessage} from "../../../model/status-message";
import StatusMessageUtils from "../../../utils/status-message-utils";
import DocumentFolderUtils from "../../../utils/document-folder-utils";
import {HttpHeaders} from "@angular/common/http";
import {CustomerInteraction} from "../../../model/customerInteraction";
import CustomerInteractionUtils from "../../../utils/customer-interaction-utils";
import {DocumentFolder} from "../../../model/documentFolder";
import {LoginIdService} from "../../../service/login-id.service";
import {StatusMessageComponent} from "../status-message/status-message.component";
import {Router} from "@angular/router";
import {InteractionStatus} from "../../../model/interactionStatus";
import {StatusMessageType} from "../../../model/enums/status-message-type";
import * as Sentry from "@sentry/angular";
import {DocumentStatusService} from "../../../service/document-status.service";

@Component({
    selector: 'app-planung-document-row',
    templateUrl: './planung-document-row.component.html',
    styleUrls: ['./planung-document-row.component.css']
})
export class PlanungDocumentRowComponent implements OnInit {

    @Input() planungDocuments: Document[] = [];
    @Input() order: Order;
    @Input() property: Property;
    @Input() customerInteractions: CustomerInteraction[];
    @Input() batchingPlanungDocumentsObservable: Subject<boolean>;
    @Output() updateBatchStarted: EventEmitter<any> = new EventEmitter();

    @ViewChild('statusMessageComponent') statusMessageComponent: StatusMessageComponent;

    columnElement: ElementRef;
    dynamicText: string = "";

    planungDocumentStatuses: DocumentStatus[] = [];
    statusMessages: StatusMessage[] = [];
    products: Product[] = [];
    productsByCategory: Map<string, Product[]> = new Map<string, Product[]>();
    planungDocumentClass: DocumentClass;
    planungDocumentClassMap: Map<string, string[]> = new Map<string, string[]>();
    showContentContainer: boolean = false;
    isLoadingStatusMessages: boolean = false;
    statusMessageInitializationError: boolean = false;
    planungCategoryNames: string[] = ["Pläne", "Satzungs- und Gebietsauskünfte", "Schutz", "ST", "Download"];
    uniqueDocumentFileGroupIds: string[] = [];

    @ViewChild('planungDownloadButton', { static: false }) set planungDownloadButtonRef(elementRef: ElementRef) {
        if (elementRef) {
            this.columnElement = elementRef;
            this.onResize();
            this.cdRef.detectChanges();
        }
    }

    getProductLongName = NameFinderUtils.getProductLongName;
    protected readonly StatusMessageUtils = StatusMessageUtils;


    constructor(private productService: ProductService, private downloadService: DownloadService,
                private cdRef: ChangeDetectorRef, private statusMessageService: StatusMessageService,
                private loginIdService: LoginIdService, private router: Router, private documentStatusService: DocumentStatusService) {
    }

    ngOnInit(): void {
        let getProductsByCategoryObservable = this.productService.getProductByCategory();
        let getDocumentClassByShortNameObservable = this.productService.getDocumentClassByShortname("PLANUN");

        //Wait for getProductsByCategoryObservable and getDocumentClassByShortNameObservable to finish to properly use initPlanungDocumentClasses()
        forkJoin([getProductsByCategoryObservable, getDocumentClassByShortNameObservable]).subscribe(([productCategoryMap, documentClass]) => {
            this.productsByCategory = productCategoryMap;
            for (const [key, value] of Object.entries(this.productsByCategory)) {
                this.products.push(...value);
            }

            this.planungDocumentClass = documentClass;
            sessionStorage.setItem("PLANUN", JSON.stringify(documentClass));

            this.initPlanungDocumentClasses();
        })

        this.initializeStatusMessages();

        //Filter duplicate entries of document status information across all planung documents an sort them by date
        this.planungDocuments.forEach(document => {
            document.statusInformation.forEach(status => {
                const isDuplicate = this.planungDocumentStatuses.some(existingStatus =>
                        existingStatus.message === status.message && existingStatus.date === status.date
                );

                if (!isDuplicate) {
                    this.planungDocumentStatuses.push(status);
                }
            });
        });
        this.planungDocumentStatuses.sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        this.batchingPlanungDocumentsObservable.subscribe((doneLoading) => {
          if(doneLoading){
            this.initUniqueDocumentFileGroupIds();
          }
        })

    }

    /**
     * Toggles the content container which holds all information regarding the document files
     * Additionally updates the toggle button in the status message component
     *
     */
    public toggleContentContainer(): void {
      this.showContentContainer = !this.showContentContainer;
      if (this.showContentContainer) {
        this.updateUnseenPlanungDocumentStatuses();
      }
      // setTimeout is used here to make sure the status message component is rendered since its shown/hidden based on the showContentContainer boolean
      setTimeout(() => {
        if(this.statusMessageComponent){
          this.statusMessageComponent.updateHistoryToggleButtonTopStyle();
        }
      })
    }

    /**
     * Marks unseen document status information as seen once the planung row is opened.
     */
    private updateUnseenPlanungDocumentStatuses(): void {
      const documentsWithUnseenInformation = this.planungDocuments.filter(planungDocument =>
          this.hasDocumentUnseenInformation(planungDocument)
      );

      if (documentsWithUnseenInformation.length < 1) {
        return;
      }

      forkJoin(documentsWithUnseenInformation.map(planungDocument =>
          this.documentStatusService.updateSeen(planungDocument.docid)
      )).subscribe({
        next: () => {
          documentsWithUnseenInformation.forEach(planungDocument => {
            planungDocument.statusInformation.forEach(statusInformation => statusInformation.seen = true);
          });
        },
        error: error => Sentry.captureException(error)
      });
    }

    /**
     * Determines if the given document contains status information that has not been seen yet.
     *
     * @param document Document which should be checked for unseen status information
     * @return True if the document contains unseen status information else false
     */
    private hasDocumentUnseenInformation(document: Document): boolean {
      return !!document.statusInformation && document.statusInformation.some(statusInformation => statusInformation.seen === false);
    }

    /**
     * Determines if any of the planung documents has at least one document file
     *
     * @return true if at least one planung document has a document file else false
     */
    public hasAnyDocumentAtLeastOneFile(): boolean {
      if (!this.isAnyDocumentStillLoading()) {
        const flattenedFolders: DocumentFolder[] = [];
        this.planungDocuments.forEach(planungDocument => flattenedFolders.push(...DocumentFolderUtils.flattenFolders(planungDocument.folders)))
        return this.planungDocuments.some(document => (document.files?.length ?? 0) > 0) ||
            flattenedFolders.some(folder => (folder.files?.length ?? 0) > 0);
      }
      return false;
    }

    /**
     * Determines if any of the planung documents is still loading its files and folders
     *
     * @return true if at least one planung document is loading its files and folders else false
     */
    public isAnyDocumentStillLoading(): boolean{
        return this.planungDocuments.some(document => document.isLoadingFilesAndFolder);
    }

    /**
     * Creates a map which maps a Category to all classNames from that category.
     * All Products from the Category "Planungsrechtsauskünfte" and Sublclasses of the DocumentClass "PLANUN" are considered
     */
    public initPlanungDocumentClasses(): void {
        //Add all document class names from products and the planung document class subclasses to iterate them easier
        const documentClassNames = new Set<string>([
            ...this.productsByCategory["Planungsrechtsauskünfte"].map((product: Product) => product.nameShort.toUpperCase()),
            ...this.planungDocumentClass.subClasses
                    .filter((subClass) => subClass.className !== "FLURB")
                    .map((subClass) => subClass.className.toUpperCase())
        ]);
        documentClassNames.forEach(className => {
            if (className === "BEBAUU" || className === "FLÄCHE") {
                const category = "Pläne";
                this.addPlanungDocumentClassToCategory(category, className);
            }
            if (className === "BID" || className === "FLURB" || className === "BESCHE" || this.planungDocumentClass.subClasses.find(subClass => subClass.className === className)) {
                const category = "Satzungs- und Gebietsauskünfte";
                this.addPlanungDocumentClassToCategory(category, className);
            }
            if (className === "NWLSCH" || className === "HWSCH" || className === "BAULSCH") {
                const category = "Schutz";
                this.addPlanungDocumentClassToCategory(category, className);
            }
            if (className === "STÄDTE") {
                const category = "ST";
                this.addPlanungDocumentClassToCategory(category, className);
            }
        })

        //Helper entry to be able to add a button at the correct position
        this.addPlanungDocumentClassToCategory("Download", "button")
    }

    /**
     * Helper function that adds a className to a specific category for this.planungDocumentClassMap
     *
     * @param category Category which functions as key for the Map
     * @param className className which functions as value for the map and should be added
     */
    public addPlanungDocumentClassToCategory(category: string, className: string): void {
        if (this.planungDocumentClassMap.has(category)) {
            const classNames = this.planungDocumentClassMap.get(category) || [];
            classNames.push(className);
            this.planungDocumentClassMap.set(category, classNames)
        } else {
            this.planungDocumentClassMap.set(category, [className])
        }
    }

    /**
     * Helper function that determines a css class for a specific category of documents
     *
     * @param categoryKey Category for which the correct css class should be determined
     * @return Classname as string
     */
    public getColumnsClass(categoryKey: string): string {
        switch (categoryKey) {
            case 'Pläne':
            case 'ST':
                return 'is-1';
            case 'Satzungs- und Gebietsauskünfte':
                return 'is-5';
            case 'Schutz':
                return 'is-2';
            case 'Download':
                return 'is-2';
            default:
                return '';
        }
    }

    /**
     * Changes the download button text based on the width of the download button element
     */
    @HostListener('window:resize', ['$event'])
    onResize() {
        if(this.columnElement){
            const columnWidth = this.columnElement.nativeElement.offsetWidth;
            if (columnWidth < 200) {
                this.dynamicText = 'Teil';
            } else {
                this.dynamicText = 'Planungsrecht Teil';
            }
        }
    }

    /**
     * Determines if a specific Document specified by its document / class name was ordered.
     * checks if this.planungDocuments contains a specific document
     *
     * @param className Name of the document for which should be checked if it was ordered or not
     * @return true if the document specified by its name was ordered else false
     */
    public isPlanungDocumentOrdered(className: string): boolean {

        //Determines if the current className is part of the planung DocumentClasses Subclasses in order to combine them to one parent class.
        //"FLURB" has to be filtered out since it has an own product
        const isSubClass = this.planungDocumentClass.subClasses
                .filter(subClass => subClass.className !== "FLURB").some(subClass => subClass.className === className);

        if (isSubClass) {
            className = "PLANUN";
        }

        return this.planungDocuments.some(document => document.docname.toUpperCase() === className.toUpperCase());
    }


    /**
     * Determines all unique fileG group ids of all files of this.planungDocuments.
     * To maintain the order of the unique group ids the earliest upload date of a document file per group id is considered
     * to sort the list.
     */
    public initUniqueDocumentFileGroupIds(): void {
        const groupIdUploadDateMap: Map<string, Date> = new Map<string, Date>();

        for (const document of this.planungDocuments) {
            for (const file of document.files) {
                if (!groupIdUploadDateMap.has(file.groupId)) {
                    groupIdUploadDateMap.set(file.groupId, file.uploadDate);
                } else {
                    const existingUploadDate = groupIdUploadDateMap.get(file.groupId);
                    if (file.uploadDate < existingUploadDate) {
                        groupIdUploadDateMap.set(file.groupId, file.uploadDate);
                    }
                }
            }
        }

        const uniqueGroupIds: Set<string> = new Set(groupIdUploadDateMap.keys());
        this.uniqueDocumentFileGroupIds = Array.from(uniqueGroupIds).sort((a, b) => {
            const dateA = new Date(groupIdUploadDateMap.get(a));
            const dateB = new Date(groupIdUploadDateMap.get(b));
            return dateA.getTime() - dateB.getTime();
        });
    }


    /**
     * Determines if there is any documentFile for a specific document / class name for a specific document file group
     *
     * @param groupId A document file group id which summarizes all document files which where uploaded at the same time
     * @param productClassName document / class name of a specific Product
     * @return  true if there is a documentFile for a document for a specific groupId
     */
    public areFilesDownloadableForGroupId(groupId: string, productClassName: string): boolean {
        let filteredDocumentFiles: DocumentFile[] = this.planungDocuments.reduce((result, document) => {
            let filteredFiles = document.files.filter(file => file.groupId === groupId);
            return result.concat(filteredFiles);
        }, []);

        return filteredDocumentFiles.some((documentFile) => {
            if (documentFile.documentSubClassNames) {
                return documentFile.documentSubClassNames.some(subClassName => subClassName.toUpperCase() === productClassName.toUpperCase())
            }
            return false;
        })

    }

    /**
     * Downloads document files for specific groupIds
     *
     * @param groupIds A list of file group ids which are used to filter which document files of which groups should be downloaded
     * @param event Event for which stopPropagation is called if the event is present
     */
    public async downloadDocumentFilesByGroupId(groupIds: string[], event: any = null) {
      if (event) {
        event.stopPropagation();
      }
      const subFolderDepth = DocumentFolderUtils.getMaxSubfolderDepthInPropertyDocuments(this.planungDocuments);
      this.updateBatchStarted.emit(await this.downloadService.downloadDocumentFilesByGroupIds(this.property, groupIds, false, this.isAnyDocumentStillLoading() || subFolderDepth >= 2))
    }

  /**
   * Determines if there are any relevant customer interactions associated with the current planung documents.
   * Relevant customer interactions have to have the status 'OPEN'
   *
   * @return true if there is at least one relevant customer interaction within this.customerInteractions else false
   */
  public hasRelevantCustomerInteraction(): boolean {
    if (!this.customerInteractions || !CustomerInteractionUtils.hasOpenInteraction(this.customerInteractions)) {
      return false;
    }
    const openCustomerInteractions = this.customerInteractions.filter(customerInteraction => customerInteraction.status === InteractionStatus.OPEN);
    return this.planungDocuments.some(document =>
        openCustomerInteractions.some(customerInteraction =>
            customerInteraction.associatedIds.includes(document.docid)
        )
    );
  }

  /**
   * Determines if the current user has download permissions for this.order
   *
   * @return True if the user has download permissions for the current order else false
   */
  public hasDownloadPermissions(): boolean {
    if (this.order) {
      return this.loginIdService.hasPermissionsForOrder(this.order, 'DOWNLOAD');
    }
  }

  /**
   * Returns a tooltip indicating that the user has no download permission if this.hasDownloadPermissions returns false
   */
  public showToolTip(): string {
    return this.hasDownloadPermissions() ? '' : 'Sie haben nicht die erforderlichen Berechtigungen, um diese Dokumente herunterzuladen!';
  }

  /**
   * Retrieves all document ids of all planung documents
   *
   * @return Array containing all document ids of all planung documents
   */
  public getPlanungDocumentIds(): string[] {
    return this.planungDocuments.map(planungDocument => planungDocument.docid);
  }

  /**
   * Navigates to the latest relevant customer interaction associated with the current planung documents.
   * If no relevant customer interaction is found nothing happens.
   *
   * @param event Event object used to stop propagation of the click event.
   */
  public navigateToCustomerInteraction(event: any): void {
    event.stopPropagation();
    const latestRelevantCustomerInteraction = CustomerInteractionUtils.getLatestCustomerInteractionAssociatedWithDocumentIds(this.customerInteractions, this.planungDocuments)
    if (!latestRelevantCustomerInteraction) {
      return
    }
    this.router.navigate(
        [`/customer-interaction/${latestRelevantCustomerInteraction.id}`],
        {state: {property: this.property}}
    );
  }

    /**
     * Initializes the status messages for the current planung documents.
     *
     * This function removes duplicate status messages if they have the same message, type and date.
     */
    private initializeStatusMessages(): void {
      this.isLoadingStatusMessages = true;

      // Create a promise to fetch status messages for each planung document
      const promises = this.planungDocuments.map(document => this.statusMessageService.getStatusMessagesByReferenceId(document.docid, {headers: new HttpHeaders({'showing-spinner': ''})}).toPromise());

      // Execute all Promises
      Promise.all(promises).then(results => {
        const allStatusMessages = results.reduce((acc, statusMessages) => acc.concat(statusMessages), []);
        let uniqueStatusMessages: StatusMessage[] = this.getUniqueStatusMessages(allStatusMessages);
        // Filter out all PhoneTicket status message event for better overview
        uniqueStatusMessages = uniqueStatusMessages.filter(statusMessage => statusMessage.type !== StatusMessageType.PHONE_TICKET_CREATED && statusMessage.type !== StatusMessageType.PHONE_TICKET_REACHED)
        this.statusMessages = this.transformStatusMessages(uniqueStatusMessages);

        // If there is only one status message for this.planungDocuments duplicate this entry so it can be correctly displayed in the status message component
        if(this.statusMessages.length === 1){
          const duplicateMessage = Object.assign({}, this.statusMessages[0]);
          this.statusMessages.push(duplicateMessage);
        }

        this.isLoadingStatusMessages = false;
        this.statusMessageInitializationError = false;
      })
      .catch(error => {
        Sentry.captureException(error);
        this.isLoadingStatusMessages = false;
        this.statusMessageInitializationError = true;
      });
    }

    /**
     * Determines if two dates are equal without considering the Time of the date.
     *
     * @param date1 First date to compare
     * @param date2 Second date to compare
     * @return True if the two given dates are equal up to the day else false
     */
      private compareDateWithoutTime(date1: Date, date2: Date): boolean {
      const dateWithoutTime = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return dateWithoutTime(date1).getTime() === dateWithoutTime(date2).getTime();
      }

      /**
       * Retrieves unique status messages from the provided array of status messages.
       * Compares the message, type and date (without seconds) of each status message.
       *
       * @param allStatusMessages Array of status messages from which all unique status messages should be retrieved
       * @private Array of all retrieved unique status messages
       */
      private getUniqueStatusMessages(allStatusMessages: StatusMessage[]): StatusMessage[] {
        const uniqueStatusMessages: StatusMessage[] = [];

        allStatusMessages.forEach(statusMessage => {
          // Check if the status message is a duplicate
          const isDuplicate = uniqueStatusMessages.some(existingStatusMessage =>
              existingStatusMessage.message === statusMessage.message &&
              existingStatusMessage.type === statusMessage.type &&
              this.compareDateWithoutTime(new Date(existingStatusMessage.date), new Date(statusMessage.date))
          )

          if (!isDuplicate) {
            uniqueStatusMessages.push(statusMessage)
          }
        });
        return uniqueStatusMessages;
      }

      /**
       * Transforms an array of status messages by transforming their dates to UTC and sorting them in descending order based on their dates
       *
       * @param statusMessages Array of status messages to transform.
       * @return New array of status messages with the updated dates and sorted in descending order based on their dates
       */
      private transformStatusMessages(statusMessages: StatusMessage[]): StatusMessage[]{
        return statusMessages
            // Map each status message to the same status message with an updated date
            .map(statusMessage => ({
              ...statusMessage,
              date: new Date(statusMessage.date)
            }))
            // Sort all status messages by their date (Desc)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      }
}
